/**
 * Grafo de conexões (aba própria). A âncora ao centro e, em volta, a rede que se liga a ela:
 * contatos compartilhados, relações, sócios, papéis, donos de imóvel/veículo e partes de processo.
 * A travessia (BFS) e a dedup são feitas no backend (`/cadastro/grafo/{tipo}/{id}`); aqui só
 * desenhamos. Layout por força (d3-force, calculado uma vez), com zoom, pan e arraste de cada nó.
 *
 * Numa base grande o grafo inteiro não é legível — é um emaranhado. Três controles cuidam disso, e
 * todos moram no backend (a tela só os manda na query):
 *
 * - **saltos**: distância máxima da âncora (padrão 2);
 * - **camadas**: correlações (mesmo telefone/e-mail/endereço), ativos e processos ligam e desligam
 *   — "mesmo endereço" é indício fraco e "sócio" é forte, e com tudo aceso pesam igual;
 * - **hub**: um nó com vizinhos demais (a empresa com 1.790 pessoas, o telefone de uma portaria)
 *   não é percorrido; vem marcado, com a contagem, e o operador CLICA para abrir.
 */
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type SimulationNodeDatum,
} from 'd3-force';
import { Maximize2, Minus, Network, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DrawerArestaGrafo,
  DrawerNoGrafo,
  type ArestaAberta,
  type ConexaoNo,
  type NoAberto,
} from '@/components/crud/DrawerNoGrafo';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import { api, type GrafoCad } from '@/lib/api';
import type { OwnerTipo } from '@/lib/recursos/tipos';

type Kind =
  | 'ancora'
  | 'pessoa'
  | 'empresa'
  | 'condominio'
  | 'imovel'
  | 'veiculo'
  | 'processo'
  | 'transacao'
  | 'titulo'
  | 'carteira'
  | 'telefone'
  | 'email'
  | 'endereco';

const COR: Record<Kind, string> = {
  ancora: '#2dc4b6',
  pessoa: '#6aa9ff',
  empresa: '#57c7a3',
  condominio: '#c99bf0',
  imovel: '#c9a15e',
  veiculo: '#5ec8f0',
  processo: '#e08a5e',
  transacao: '#e0c14e',
  titulo: '#d4b483',
  carteira: '#b08968',
  telefone: '#f0a35e',
  email: '#e0679b',
  endereco: '#7ed957',
};
/** Cores dos NÚCLEOS. Deliberadamente distintas das de tipo: quando a camada está ligada, o que a
 *  cor quer dizer muda, e duas leituras diferentes com a mesma paleta se confundiriam. Atribuídas
 *  na ordem em que os núcleos aparecem, e a ordem dos nós já é determinística no backend — a mesma
 *  tela recarregada mantém as mesmas cores. */
const CORES_NUCLEO = [
  '#e0526e',
  '#4e9bf5',
  '#3fbf7f',
  '#d99b2b',
  '#a06ee0',
  '#2fb8b0',
  '#e07a3f',
  '#8fbf3f',
];

/** Cinza para o nó que não está em núcleo nenhum: ele não é um erro, é alguém que não se agrupa —
 *  e destacá-lo com uma cor viva competiria com o que a camada existe para mostrar. */
const COR_SEM_NUCLEO = '#6b7280';

const ROTULO_KIND: Record<Kind, string> = {
  ancora: 'Âncora',
  pessoa: 'Pessoa',
  empresa: 'Empresa',
  condominio: 'Condomínio',
  imovel: 'Imóvel',
  veiculo: 'Veículo',
  processo: 'Processo',
  transacao: 'Transação',
  titulo: 'Recebível',
  carteira: 'Carteira',
  telefone: 'Telefone',
  email: 'E-mail',
  endereco: 'Endereço',
};

const RAIO: Record<Kind, number> = {
  ancora: 12,
  pessoa: 8,
  empresa: 8,
  condominio: 8,
  imovel: 8,
  veiculo: 8,
  processo: 8,
  transacao: 8,
  titulo: 8,
  carteira: 10,
  telefone: 5,
  email: 5,
  endereco: 5,
};

interface No extends SimulationNodeDatum {
  id: string;
  kind: Kind;
  rotulo: string;
  /** Vizinhos que o nó tem de verdade (não os que couberam na tela). */
  grau: number;
  /** Nó de grau alto que não foi percorrido — desenhado com anel tracejado e clicável para abrir. */
  hub: boolean;
  /** A que núcleo econômico o nó pertence, quando a camada está ligada; `null` = a nenhum. */
  nucleoId: string | null;
}
interface Aresta {
  source: string | No;
  target: string | No;
  rotulo?: string;
}

const W = 720;
const H = 520;

/** Converte o grafo pronto do backend nos nós/arestas do d3 (a âncora vira o kind 'ancora'). */
function converterGrafo(g: GrafoCad): { nos: No[]; arestas: Aresta[] } {
  const nos: No[] = g.nos.map((n) => ({
    id: n.id,
    kind: (n.id === g.ancora ? 'ancora' : (n.tipo as Kind)) satisfies Kind,
    rotulo: n.rotulo,
    grau: n.grau ?? 0,
    hub: !!n.hub,
    nucleoId: n.nucleo_id ?? null,
  }));
  const arestas: Aresta[] = g.arestas.map((a) => ({
    source: a.origem,
    target: a.destino,
    rotulo: a.rotulo ?? undefined,
  }));
  return { nos, arestas };
}

/** O nome de um núcleo na legenda: o rótulo do membro que o representa, e quantos são.
 *  `empresa:019ff8…` é um id honesto e não diz nada a quem lê a tela. */
function rotuloNucleo(grafo: { nos: No[] } | null, id: string): string {
  const membros = (grafo?.nos ?? []).filter((n) => n.nucleoId === id);
  const cabeca = membros.find((n) => n.id === id) ?? membros[0];
  return `${corte(cabeca?.rotulo ?? id, 20)} · ${membros.length}`;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const corte = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Roda a simulação de força até estabilizar (sem loop de animação); a âncora fica fixa no centro.
 * Os parâmetros ESCALAM com o número de nós: quanto maior a rede, mais repulsão e arestas mais
 * longas, para o grafo multinível não empilhar tudo num aglomerado. */
function calcularLayout(nos: No[], arestas: Aresta[]): No[] {
  const n = nos.length;
  const ancora = nos.find((no) => no.kind === 'ancora');
  if (ancora) {
    ancora.fx = W / 2;
    ancora.fy = H / 2;
  }
  // Rótulos (nomes, endereços) ocupam largura; a colisão reserva espaço além do círculo para eles.
  const raioColisao = (no: No) => RAIO[no.kind] + 14 + Math.min(70, corte(no.rotulo, 22).length * 3);
  const distancia = clamp(64 + n * 2.4, 64, 190);
  const repulsao = -(220 + n * 26);
  const sim = forceSimulation(nos)
    .force(
      'link',
      forceLink<No, Aresta>(arestas)
        .id((no) => no.id)
        .distance(distancia)
        .strength(0.65),
    )
    .force('charge', forceManyBody().strength(repulsao).distanceMax(520))
    .force('center', forceCenter(W / 2, H / 2))
    .force('collide', forceCollide<No>(raioColisao).strength(0.9))
    .stop();
  for (let i = 0; i < 460; i++) sim.tick();
  return nos;
}

/** Enquadra o grafo inteiro na viewport: centraliza e ajusta o zoom para tudo caber com folga. */
function enquadrar(nos: No[]): { k: number; x: number; y: number } {
  const xs = nos.map((no) => no.x ?? W / 2);
  const ys = nos.map((no) => no.y ?? H / 2);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const margem = 70; // espaço para os rótulos que saem para os lados dos nós das bordas
  const larg = Math.max(1, maxX - minX);
  const alt = Math.max(1, maxY - minY);
  const k = clamp(Math.min((W - margem * 2) / larg, (H - margem * 2) / alt), 0.3, 1.3);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  return { k, x: W / 2 - cx * k, y: H / 2 - cy * k };
}

export function PainelGrafo({
  ownerTipo,
  ownerId,
  /** Rota alternativa da travessia, sem os filtros de query — usada quando a âncora NÃO é uma
   * entidade e sim um recurso compartilhado (`/cadastro/auxiliares/telefones/{id}/grafo`). O
   * desenho é o mesmo; o que muda é quem está no centro. */
  base,
}: {
  ownerTipo: OwnerTipo;
  ownerId: string;
  base?: string;
}) {
  const [dados, setDados] = useState<GrafoCad | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [saltos, setSaltos] = useState(2);
  const [correlacoes, setCorrelacoes] = useState(true);
  const [ativos, setAtivos] = useState(true);
  const [processos, setProcessos] = useState(true);
  // A camada de núcleo é OUTRA leitura da mesma tela: em vez de «o que é cada coisa», «quem é o
  // mesmo ator». Por isso ela troca o significado da cor em vez de somar nós — o sócio comum que
  // forma o núcleo já é uma aresta ali.
  const [nucleo, setNucleo] = useState(false);
  // Hubs que o operador mandou abrir. Some ao trocar de âncora ou de filtro estrutural.
  const [expandidos, setExpandidos] = useState<string[]>([]);
  // Nó sob o ponteiro: o resto do grafo esmaece, para a vizinhança dele saltar aos olhos.
  const [focado, setFocado] = useState<string | null>(null);
  // A PILHA de nós abertos no drawer. Pilha, e não um id só, porque puxar um fio é encadeado:
  // pessoa → telefone → outra pessoa, e o operador precisa poder voltar sem recomeçar.
  const [trilha, setTrilha] = useState<string[]>([]);
  // A aresta aberta. Separada da trilha porque uma ligação não é um nó: ela não tem ficha, tem
  // duas pontas — e clicar numa delas devolve o operador ao fluxo normal, pela trilha.
  const [arestaAberta, setArestaAberta] = useState<ArestaAberta | null>(null);
  const [vista, setVista] = useState({ k: 1, x: 0, y: 0 });
  // Posições sobrescritas pelo arraste de nós (id → {x,y} em coordenadas do mundo).
  const [posLivre, setPosLivre] = useState<Record<string, { x: number; y: number }>>({});
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panRef = useRef<{ x: number; y: number } | null>(null);
  const dragNo = useRef<{ id: string; dx: number; dy: number } | null>(null);
  // Onde o ponteiro desceu, e sobre O QUÊ — a origem do gesto, para separar clique de arraste.
  // Vale para nó e para aresta: os dois são clicáveis, e nos dois o `click` do DOM é engolido pelo
  // `setPointerCapture` que faz o arraste (do nó) e o pan (do fundo) funcionarem.
  const pressao = useRef<{ no?: No; aresta?: Aresta; x: number; y: number } | null>(null);
  // Tooltip de hover: detalhes da correlação (nó ou aresta) na posição do ponteiro.
  const [dica, setDica] = useState<{ x: number; y: number; titulo: string; linhas: string[] } | null>(
    null,
  );

  // Trocar de âncora ou de camada zera os hubs abertos: eles falam de um grafo que não é mais este.
  // A trilha do drawer some pelo mesmo motivo — o nó aberto pode nem existir no grafo novo.
  useEffect(() => {
    setExpandidos([]);
    setTrilha([]);
    setArestaAberta(null);
  }, [ownerTipo, ownerId, correlacoes, ativos, processos]);

  useEffect(() => {
    setDados(null);
    setPosLivre({});
    setVista({ k: 1, x: 0, y: 0 });
    if (!ownerId) return;
    let vivo = true;
    setCarregando(true);
    const q = new URLSearchParams({
      saltos: String(saltos),
      correlacoes: String(correlacoes),
      ativos: String(ativos),
      processos: String(processos),
      nucleo: String(nucleo),
    });
    for (const id of expandidos) q.append('expandir', id);
    const rota = base ?? `/cadastro/grafo/${ownerTipo}/${ownerId}`;
    void api.get<GrafoCad>(`${rota}?${q}`).then((r) => {
      if (!vivo) return;
      setCarregando(false);
      if (r.ok) setDados(r.dados);
    });
    return () => {
      vivo = false;
    };
  }, [ownerTipo, ownerId, base, saltos, correlacoes, ativos, processos, nucleo, expandidos]);

  const grafo = useMemo(() => {
    if (!dados) return null;
    const { nos, arestas } = converterGrafo(dados);
    calcularLayout(nos, arestas); // muta nos (x,y) e arestas (source/target viram objetos)
    return { nos, arestas };
  }, [dados]);

  const temGrafo = !!grafo && grafo.nos.length > 1;

  // Ao (re)montar o grafo, enquadra tudo na viewport — nada de começar cortado ou amontoado.
  useEffect(() => {
    if (grafo && grafo.nos.length > 1) {
      setPosLivre({});
      setVista(enquadrar(grafo.nos));
    }
  }, [grafo]);

  // Ponto (clientX/Y) → coordenadas do viewBox.
  const paraViewBox = (cx: number, cy: number) => {
    const r = svgRef.current!.getBoundingClientRect();
    return { vx: ((cx - r.left) / r.width) * W, vy: ((cy - r.top) / r.height) * H };
  };
  // viewBox → mundo (desfaz o transform de zoom/pan).
  const paraMundo = (vx: number, vy: number) => ({
    x: (vx - vista.x) / vista.k,
    y: (vy - vista.y) / vista.k,
  });

  // Zoom com a roda, ancorado no ponteiro. Re-anexa quando o SVG monta (dep em temGrafo).
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !temGrafo) return;
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const r = svg!.getBoundingClientRect();
      const mx = ((e.clientX - r.left) / r.width) * W;
      const my = ((e.clientY - r.top) / r.height) * H;
      setVista((v) => {
        const k = clamp(v.k * (e.deltaY < 0 ? 1.12 : 1 / 1.12), 0.3, 5);
        return { k, x: mx - ((mx - v.x) / v.k) * k, y: my - ((my - v.y) / v.k) * k };
      });
    }
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [temGrafo]);

  const zoom = (fator: number) =>
    setVista((v) => {
      const k = clamp(v.k * fator, 0.3, 5);
      return { k, x: W / 2 - ((W / 2 - v.x) / v.k) * k, y: H / 2 - ((H / 2 - v.y) / v.k) * k };
    });

  const px = (n: No) => posLivre[n.id]?.x ?? n.x ?? W / 2;
  const py = (n: No) => posLivre[n.id]?.y ?? n.y ?? H / 2;

  function noPointerDownNo(e: React.PointerEvent, n: No) {
    e.stopPropagation();
    const { vx, vy } = paraViewBox(e.clientX, e.clientY);
    const m = paraMundo(vx, vy);
    dragNo.current = { id: n.id, dx: px(n) - m.x, dy: py(n) - m.y };
    // Onde o dedo/ponteiro desceu: o `pointerup` compara para decidir se foi CLIQUE ou ARRASTE.
    // Não dá para usar `onClick`: o `setPointerCapture` abaixo (que faz o arraste funcionar fora
    // do círculo) engole o evento de clique em parte dos navegadores. E o critério de distância
    // é necessário de qualquer forma — arrastar um nó para reposicioná-lo não pode abrir nada.
    pressao.current = { no: n, x: e.clientX, y: e.clientY };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }
  function onPointerDownFundo(e: React.PointerEvent) {
    panRef.current = { x: e.clientX - vista.x, y: e.clientY - vista.y };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (dragNo.current) {
      const { vx, vy } = paraViewBox(e.clientX, e.clientY);
      const m = paraMundo(vx, vy);
      const d = dragNo.current;
      setPosLivre((p) => ({ ...p, [d.id]: { x: m.x + d.dx, y: m.y + d.dy } }));
    } else if (panRef.current) {
      // Captura o pan e as coordenadas ANTES do updater: um pointerup/leave concorrente pode zerar
      // panRef.current antes de o updater rodar, e desreferenciá-lo lá dentro (o `!`) crashava a tela.
      const pan = panRef.current;
      const { clientX, clientY } = e;
      setVista((v) => ({ ...v, x: clientX - pan.x, y: clientY - pan.y }));
    }
  }
  /** Movimento abaixo disto ainda é "clique": ninguém acerta um pixel, nem no trackpad. */
  const TOLERANCIA_CLIQUE = 4;

  function onPointerUp(e?: React.PointerEvent) {
    const p = pressao.current;
    if (p && e && Math.hypot(e.clientX - p.x, e.clientY - p.y) <= TOLERANCIA_CLIQUE) {
      if (p.no) setTrilha([p.no.id]);
      else if (p.aresta) abrirAresta(p.aresta);
    }
    pressao.current = null;
    dragNo.current = null;
    panRef.current = null;
  }

  /** Pressionar uma aresta: guarda o gesto e IMPEDE o pan — arrastar sobre a linha moveria a tela
   * inteira, e ao soltar o operador veria um drawer que não pediu. */
  function noPointerDownAresta(e: React.PointerEvent, a: Aresta) {
    e.stopPropagation();
    pressao.current = { aresta: a, x: e.clientX, y: e.clientY };
  }

  const kinds = useMemo(() => [...new Set(grafo?.nos.map((n) => n.kind))] as Kind[], [grafo]);

  // Núcleo → cor. A ordem de atribuição é a de aparição, e a ordem dos nós já vem determinística do
  // backend: a mesma tela recarregada mantém as mesmas cores.
  const coresNucleo = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const n of grafo?.nos ?? []) {
      if (n.nucleoId && !mapa.has(n.nucleoId)) {
        mapa.set(n.nucleoId, CORES_NUCLEO[mapa.size % CORES_NUCLEO.length]);
      }
    }
    return mapa;
  }, [grafo]);

  /** A cor do nó. Com a camada ligada, a cor deixa de dizer «o que é» e passa a dizer «com quem
   *  é» — a âncora continua com a cor dela, porque perder de vista o que se pediu para ver
   *  custaria mais do que a coerência da paleta. */
  const corDo = (n: No) =>
    !nucleo || n.kind === 'ancora'
      ? COR[n.kind]
      : (n.nucleoId ? coresNucleo.get(n.nucleoId) : null) ?? COR_SEM_NUCLEO;

  // Posição do tooltip relativa ao wrapper do SVG (offset do ponteiro).
  function posDica(e: React.PointerEvent): { x: number; y: number } {
    const r = wrapRef.current?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  }
  /** Conexões de um nó: os outros extremos das arestas que o tocam, com o tipo do vínculo. */
  function conexoesDe(id: string): string[] {
    const out: string[] = [];
    for (const a of grafo?.arestas ?? []) {
      const s = a.source as No;
      const t = a.target as No;
      const outro = s.id === id ? t : t.id === id ? s : null;
      if (outro) out.push(a.rotulo ? `${outro.rotulo} (${a.rotulo})` : outro.rotulo);
    }
    return out;
  }
  function dicaNo(e: React.PointerEvent, n: No) {
    if (dragNo.current) return;
    const cx = conexoesDe(n.id);
    const linhas = [ROTULO_KIND[n.kind]];
    if (n.hub) {
      // O hub precisa dizer o que ESCONDE, senão a tela dá por completo o que não está.
      const fora = dados?.omitidos?.[n.id] ?? n.grau;
      linhas.push(`${n.grau} vínculos — ${fora} fora da tela`);
    }
    if (cx.length) {
      linhas.push(...cx.slice(0, 6).map((c) => `↔ ${c}`));
      if (cx.length > 6) linhas.push(`+${cx.length - 6} conexões`);
    }
    linhas.push('clique para ver os dados');
    setDica({ ...posDica(e), titulo: n.rotulo, linhas });
  }

  /** O nó no topo da trilha, já com os dados do grafo desenhado. `null` = drawer fechado. */
  const noAberto: NoAberto | null = useMemo(() => {
    const id = trilha[trilha.length - 1];
    const n = id ? grafo?.nos.find((x) => x.id === id) : undefined;
    return n ? { id: n.id, rotulo: n.rotulo, tipo: n.kind, grau: n.grau, hub: n.hub } : null;
  }, [trilha, grafo]);

  /** Conexões de um nó, prontas para o drawer: o vizinho e COMO se chega nele. */
  const conexoesDoNo = useCallback(
    (id: string): ConexaoNo[] => {
      const vistos = new Set<string>();
      const out: ConexaoNo[] = [];
      for (const a of grafo?.arestas ?? []) {
        const s = a.source as No;
        const alvo = a.target as No;
        const outro = s.id === id ? alvo : alvo.id === id ? s : null;
        if (!outro || vistos.has(outro.id)) continue;
        vistos.add(outro.id);
        // `kind` da âncora é 'ancora' — um estado da tela, não um tipo de coisa. Quem lê a lista
        // quer saber que é uma Pessoa; o id sempre traz o tipo verdadeiro no prefixo.
        const tipo = outro.kind === 'ancora' ? outro.id.slice(0, outro.id.indexOf(':')) : outro.kind;
        out.push({ id: outro.id, rotulo: outro.rotulo, tipo, via: a.rotulo ?? null });
      }
      return out;
    },
    [grafo],
  );

  /** Abre um hub (ou o fecha de volta) — a régua não esconde dado, só espera o operador pedir. */
  const alternarHub = useCallback((id: string) => {
    setExpandidos((atuais) =>
      atuais.includes(id) ? atuais.filter((x) => x !== id) : [...atuais, id],
    );
  }, []);

  // Vizinhança do nó sob o ponteiro: o que não a toca esmaece. É o que torna densa legível.
  const vizinhanca = useMemo(() => {
    if (!focado || !grafo) return null;
    const perto = new Set<string>([focado]);
    for (const a of grafo.arestas) {
      const s = (a.source as No).id;
      const t = (a.target as No).id;
      if (s === focado) perto.add(t);
      else if (t === focado) perto.add(s);
    }
    return perto;
  }, [focado, grafo]);
  const totalOmitido = useMemo(
    () => Object.values(dados?.omitidos ?? {}).reduce((a, b) => a + b, 0),
    [dados],
  );
  const opacidadeNo = (id: string) => (!vizinhanca || vizinhanca.has(id) ? 1 : 0.12);
  const opacidadeAresta = (a: Aresta) => {
    if (!vizinhanca) return 0.45;
    const s = (a.source as No).id;
    const t = (a.target as No).id;
    return s === focado || t === focado ? 0.9 : 0.06;
  };
  function dicaAresta(e: React.PointerEvent, a: Aresta) {
    const s = a.source as No;
    const t = a.target as No;
    setDica({
      ...posDica(e),
      titulo: a.rotulo ?? 'compartilha',
      linhas: [`${s.rotulo}  ↔  ${t.rotulo}`],
    });
  }

  /** Abre a ligação: quem são as duas pontas. O rótulo da aresta é o "por quê" da ligação. */
  function abrirAresta(a: Aresta) {
    const s = a.source as No;
    const t = a.target as No;
    setArestaAberta({
      rotulo: a.rotulo ?? 'Compartilham um contato',
      pontas: [s, t].map((n) => ({
        id: n.id,
        rotulo: n.rotulo,
        tipo: n.kind === 'ancora' ? n.id.slice(0, n.id.indexOf(':')) : n.kind,
        via: null,
      })),
    });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Network className="size-4 text-accent" />
          Grafo de conexões
        </CardTitle>
        {temGrafo && (
          <div className="flex items-center gap-1">
            <BotaoZoom aria-label="Menos zoom" onClick={() => zoom(1 / 1.2)}>
              <Minus className="size-4" />
            </BotaoZoom>
            <BotaoZoom aria-label="Mais zoom" onClick={() => zoom(1.2)}>
              <Plus className="size-4" />
            </BotaoZoom>
            <BotaoZoom
              aria-label="Reajustar"
              onClick={() => {
                setPosLivre({});
                if (grafo) setVista(enquadrar(grafo.nos));
              }}
            >
              <Maximize2 className="size-4" />
            </BotaoZoom>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-border border-b pb-3">
          <label className="flex items-center gap-1.5 tipo-corpo-pequeno text-text-secondary">
            Saltos
            <select
              value={saltos}
              onChange={(e) => setSaltos(Number(e.target.value))}
              className="rounded-md border border-border bg-surface px-1.5 py-0.5 text-text outline-none focus-visible:shadow-[var(--focus-ring)]"
            >
              {[1, 2, 3, 4].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <Alternador ligado={correlacoes} aoMudar={setCorrelacoes} rotulo="Correlações" />
          <Alternador ligado={ativos} aoMudar={setAtivos} rotulo="Imóveis e veículos" />
          <Alternador ligado={processos} aoMudar={setProcessos} rotulo="Processos, transações e recebíveis" />
          <Alternador ligado={nucleo} aoMudar={setNucleo} rotulo="Colorir por núcleo" />
          {expandidos.length > 0 && (
            <button
              type="button"
              onClick={() => setExpandidos([])}
              className="tipo-corpo-pequeno text-accent underline-offset-2 hover:underline"
            >
              fechar {expandidos.length} nó(s) aberto(s)
            </button>
          )}
        </div>
        {carregando ? (
          <Spinner />
        ) : !temGrafo ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Sem conexões ainda — conforme a base cresce, o grafo se forma.
          </p>
        ) : (
          <>
            <div ref={wrapRef} className="relative">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              className="h-[68vh] max-h-[680px] w-full touch-none rounded-md border border-border bg-surface-2"
              onPointerDown={onPointerDownFundo}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={() => {
                // Sair do SVG cancela o gesto: sem o evento não há como saber se andou, e abrir o
                // drawer "por via das dúvidas" seria abrir sozinho no meio de um arraste.
                pressao.current = null;
                onPointerUp();
                setDica(null);
              }}
              role="img"
              aria-label="Grafo de conexões"
            >
              <g transform={`translate(${vista.x},${vista.y}) scale(${vista.k})`}>
                {grafo!.arestas.map((a, i) => {
                  const s = a.source as No;
                  const t = a.target as No;
                  const mx = (px(s) + px(t)) / 2;
                  const my = (py(s) + py(t)) / 2;
                  return (
                    <g
                      key={`e${i}`}
                      onPointerEnter={(e) => dicaAresta(e, a)}
                      onPointerMove={(e) => dicaAresta(e, a)}
                      onPointerLeave={() => setDica(null)}
                      onPointerDown={(e) => noPointerDownAresta(e, a)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Linha larga invisível: alvo de hover generoso sobre a aresta fina. */}
                      <line
                        x1={px(s)}
                        y1={py(s)}
                        x2={px(t)}
                        y2={py(t)}
                        stroke="transparent"
                        strokeWidth={10}
                      />
                      <line
                        x1={px(s)}
                        y1={py(s)}
                        x2={px(t)}
                        y2={py(t)}
                        stroke="var(--text-tertiary)"
                        strokeOpacity={opacidadeAresta(a)}
                        strokeWidth={1.2}
                        style={{ pointerEvents: 'none' }}
                      />
                      {a.rotulo && !vizinhanca && (
                        <text
                          x={mx}
                          y={my}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="fill-text-secondary"
                          style={{
                            fontSize: 9.5,
                            pointerEvents: 'none',
                            // Halo na cor do fundo para o rótulo ler sobre as linhas e outros nós.
                            paintOrder: 'stroke',
                            stroke: 'var(--surface-2)',
                            strokeWidth: 3,
                            strokeLinejoin: 'round',
                          }}
                        >
                          {a.rotulo}
                        </text>
                      )}
                    </g>
                  );
                })}
                {grafo!.nos.map((n) => {
                  const dir = px(n) >= W / 2 ? 1 : -1;
                  return (
                    <g
                      key={n.id}
                      onPointerDown={(e) => noPointerDownNo(e, n)}
                      onPointerEnter={(e) => {
                        dicaNo(e, n);
                        setFocado(n.id);
                      }}
                      onPointerMove={(e) => dicaNo(e, n)}
                      onPointerLeave={() => {
                        setDica(null);
                        setFocado(null);
                      }}
                      // A abertura acontece no `pointerup` (ver `noPointerDownNo`), e vale para
                      // QUALQUER nó — inclusive o hub, cuja expansão virou um botão dentro do
                      // drawer. Um clique com dois significados conforme o tipo do nó é o tipo de
                      // regra que o operador só aprende errando.
                      style={{ cursor: 'pointer', opacity: opacidadeNo(n.id) }}
                    >
                      {/* Anel tracejado: este nó tem mais vizinhos do que a tela mostra. */}
                      {n.hub && (
                        <circle
                          cx={px(n)}
                          cy={py(n)}
                          r={RAIO[n.kind] + 4.5}
                          fill="none"
                          stroke={corDo(n)}
                          strokeWidth={1.5}
                          strokeDasharray="3 2.5"
                        />
                      )}
                      <circle
                        cx={px(n)}
                        cy={py(n)}
                        r={RAIO[n.kind]}
                        fill={corDo(n)}
                        stroke="var(--surface-2)"
                        strokeWidth={1.5}
                      />
                      <text
                        x={px(n) + dir * (RAIO[n.kind] + (n.hub ? 9 : 5))}
                        y={py(n)}
                        textAnchor={dir > 0 ? 'start' : 'end'}
                        dominantBaseline="middle"
                        className="fill-text"
                        style={{
                          fontSize: n.kind === 'ancora' ? 13 : 11,
                          fontWeight: n.kind === 'ancora' ? 600 : 400,
                          pointerEvents: 'none',
                          // Halo na cor do fundo: o rótulo lê sobre as linhas e outros nós.
                          paintOrder: 'stroke',
                          stroke: 'var(--surface-2)',
                          strokeWidth: 3,
                          strokeLinejoin: 'round',
                        }}
                      >
                        {corte(n.rotulo, 24)}
                        {n.hub ? ` +${dados?.omitidos?.[n.id] ?? n.grau}` : ''}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
            {dica && (
              <div
                className="pointer-events-none absolute z-[var(--z-tooltip)] max-w-64 rounded-md border border-border bg-surface px-2.5 py-1.5 shadow-[var(--shadow-md)]"
                style={{
                  left: Math.min(dica.x + 14, (wrapRef.current?.clientWidth ?? 0) - 180),
                  top: dica.y + 14,
                }}
              >
                <p className="truncate text-[13px] font-medium text-text">{dica.titulo}</p>
                {dica.linhas.map((l, i) => (
                  <p key={i} className="truncate tipo-corpo-pequeno text-text-tertiary">
                    {l}
                  </p>
                ))}
              </div>
            )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {/* A legenda segue o que a cor está dizendo AGORA: com a camada ligada, listar tipos
                  seria descrever uma paleta que não está mais na tela. */}
              {nucleo
                ? [
                    ...[...coresNucleo].map(([id, cor]) => (
                      <span
                        key={id}
                        className="flex items-center gap-1.5 tipo-corpo-pequeno text-text-tertiary"
                      >
                        <span className="size-2.5 rounded-full" style={{ background: cor }} />
                        {rotuloNucleo(grafo, id)}
                      </span>
                    )),
                    <span
                      key="sem-nucleo"
                      className="flex items-center gap-1.5 tipo-corpo-pequeno text-text-tertiary"
                    >
                      <span
                        className="size-2.5 rounded-full"
                        style={{ background: COR_SEM_NUCLEO }}
                      />
                      Sem núcleo
                    </span>,
                  ]
                : kinds.map((k) => (
                    <span
                      key={k}
                      className="flex items-center gap-1.5 tipo-corpo-pequeno text-text-tertiary"
                    >
                      <span className="size-2.5 rounded-full" style={{ background: COR[k] }} />
                      {ROTULO_KIND[k]}
                    </span>
                  ))}
              <span className="tipo-corpo-pequeno text-text-tertiary">
                · role p/ ampliar · arraste o fundo p/ mover · arraste um nó p/ reposicionar
              </span>
            </div>
            {(dados?.truncado || totalOmitido > 0) && (
              <p className="tipo-corpo-pequeno text-text-tertiary">
                {totalOmitido > 0 && (
                  <>
                    <strong className="font-medium text-text-secondary">{totalOmitido}</strong>{' '}
                    conexões fora da tela, em {Object.keys(dados?.omitidos ?? {}).length} nó(s) de
                    grau alto — clique num nó de anel tracejado para abrir.{' '}
                  </>
                )}
                {dados?.truncado && 'A rede excedeu o teto da travessia; aumente os saltos com cautela.'}
              </p>
            )}
          </>
        )}
      </CardContent>

      {arestaAberta && !noAberto && (
        <DrawerArestaGrafo
          aresta={arestaAberta}
          onFechar={() => setArestaAberta(null)}
          onIrPara={(id) => {
            setArestaAberta(null);
            setTrilha([id]);
          }}
        />
      )}

      {noAberto && (
        <DrawerNoGrafo
          no={noAberto}
          conexoes={conexoesDoNo(noAberto.id)}
          onFechar={() => setTrilha([])}
          onIrPara={(id) => setTrilha((atual) => [...atual, id])}
          onVoltar={() => setTrilha((atual) => atual.slice(0, -1))}
          podeVoltar={trilha.length > 1}
          onAbrirHub={
            noAberto.hub
              ? () => {
                  // Revelar os vizinhos redesenha o grafo atrás; manter o drawer aberto por cima
                  // esconderia justamente o que o operador pediu para ver.
                  alternarHub(noAberto.id);
                  setTrilha([]);
                }
              : undefined
          }
        />
      )}
    </Card>
  );
}

/** Liga/desliga uma camada do grafo. Correlação por contato é indício FRACO — poder apagá-la da
 * tela é o que deixa os vínculos declarados (sócio, paciente, dono) aparecerem sozinhos. */
function Alternador({
  ligado,
  aoMudar,
  rotulo,
}: {
  ligado: boolean;
  aoMudar: (v: boolean) => void;
  rotulo: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 tipo-corpo-pequeno text-text-secondary">
      <input
        type="checkbox"
        checked={ligado}
        onChange={(e) => aoMudar(e.target.checked)}
        className="size-3.5 accent-accent"
      />
      {rotulo}
    </label>
  );
}

function BotaoZoom({
  children,
  onClick,
  'aria-label': ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  'aria-label': string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="rounded-md border border-border p-1.5 text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text focus-visible:shadow-[var(--focus-ring)]"
    >
      {children}
    </button>
  );
}
